import { Arrow, Tooltip } from '@studio/design-system/tooltip';
import { motion } from 'framer-motion';
import type { ReactElement, ReactNode } from 'react';

export type AutoTrackingTooltipVariant = 'free-plan' | 'embedded-only' | 'bridge-offline';

export interface AutoTrackingTooltipProps {
  variant: AutoTrackingTooltipVariant;
  children?: ReactNode;
  trigger?: ReactNode;
  zIndex?: number;
}

/** Auto Tracking tooltip component matching Figma design specs. */
export const AutoTrackingTooltip = ({
  variant,
  children,
  trigger,
  zIndex = 100
}: AutoTrackingTooltipProps): ReactElement => {
  const content =
    variant === 'free-plan' ? (
      <>
        <div className='text-sm font-demibold leading-[18px] text-black'>
          Upgrade to unlock tracking
        </div>
        <div className='text-sm font-medium leading-[18px] text-black'>
          Auto-Tracking isn't available on the Free plan. Upgrade your Studio to enable it.
        </div>
      </>
    ) : variant === 'bridge-offline' ? (
      <>
        <div className='text-sm font-demibold leading-[18px] text-black'>
          Auto-Tracking Disabled
        </div>
        <div className='text-sm font-medium leading-[18px] text-black'>
          Link a desktop bridge in order to enable Auto Tracking
        </div>
      </>
    ) : (
      <>
        <div className='text-sm font-demibold leading-[18px] text-black'>
          Auto-Tracking Disabled
        </div>
        <div className='text-sm font-medium leading-[18px] text-black'>
          Your studio has an Iris-Enabled device without a paired Bridge. Link a Bridge to enable
          Auto-Tracking.
        </div>
      </>
    );

  // Support both children and trigger props, with children taking precedence
  const triggerElement = children ?? trigger;

  return (
    <Tooltip zIndex={zIndex} trigger={triggerElement}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.2 }}
      >
        <div className='w-[330px] rounded-8 bg-[#F6F7F9] p-18 text-left'>
          <div className='flex flex-col gap-4'>{content}</div>
          <Arrow className='fill-[#F6F7F9]' />
        </div>
      </motion.div>
    </Tooltip>
  );
};
